import type { SxProps, Theme } from '@mui/material';

function applyButtonStyles(overrides: Record<string, unknown> = {}): SxProps<Theme> {
  return {
    minHeight: '58px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    border: 0,
    borderRadius: '7px',
    padding: '0 18px 0 14px',
    background: '#098beb',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 400,
    cursor: 'pointer',
    textTransform: 'none',
    '&.MuiButton-root:hover': {
      background: '#098beb',
    },
    '&:disabled, &.MuiButton-root.Mui-disabled': {
      background: '#8fbfe5',
      color: '#ffffff',
      cursor: 'default',
    },
    '& .MuiButton-startIcon': {
      width: '38px',
      height: '38px',
      display: 'grid',
      placeItems: 'center',
      margin: 0,
      border: '3px solid #ffffff',
      borderRadius: '999px',
    },
    '& .MuiButton-startIcon .MuiSvgIcon-root, & .MuiButton-startIcon .skill-svg-icon': {
      width: '24px',
      height: '24px',
      fill: 'currentColor',
      stroke: 'none',
    },
    ...overrides,
  } as SxProps<Theme>;
}

export const skillCheckNoticeClassNames = {
  message: 'skill-check-message',
  action: 'skill-check-action',
  notice: 'skill-check-notice',
  canConfirm: 'can-confirm',
  blocked: 'blocked',
  copy: 'skill-check-notice-copy',
  title: 'skill-check-notice-title',
  refreshButton: 'skill-check-refresh-button',
  applyButton: 'skill-apply-button',
  anywayButton: 'skill-anyway-button',
};

export const skillCheckNoticeStyles = {
  message: () =>
    ({
      width: 'min(592px, 100%)',
      margin: '0 0 12px',
      border: '1px solid #d92d20',
      borderRadius: '8px',
      padding: '10px 12px',
      background: '#fff1f0',
      color: '#7a271a',
      fontWeight: 600,
    }) as SxProps<Theme>,

  action: () =>
    ({
      display: 'grid',
      justifyItems: 'center',
      gap: '14px',
      width: 'min(680px, 100%)',
      margin: '14px auto 0',
    }) as SxProps<Theme>,

  notice: (theme: Theme, teacherCanConfirm: boolean) =>
    ({
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      gap: '12px',
      alignItems: 'start',
      width: '100%',
      border: teacherCanConfirm ? '1px solid #f0c98b' : '1px solid transparent',
      borderRadius: '8px',
      padding: '12px 14px',
      background: teacherCanConfirm ? '#fff8ec' : '#fff0f0',
      color: teacherCanConfirm ? '#6f3618' : '#7a0c0c',
      '& .MuiAlert-icon, & .MuiAlert-message, & .MuiAlert-action': {
        padding: 0,
      },
      '& .MuiAlert-icon': {
        color: 'inherit',
      },
      '& .MuiAlert-message': {
        minWidth: 0,
        overflow: 'visible',
      },
      '& .MuiAlert-action': {
        alignItems: 'flex-start',
        margin: 0,
      },
      '& .MuiAlert-icon .MuiSvgIcon-root': {
        fill: 'currentColor',
        stroke: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        '& .MuiAlert-action': {
          gridColumn: 2,
          justifySelf: 'start',
        },
      },
    }) as SxProps<Theme>,

  copy: () =>
    ({
      display: 'grid',
      gap: '7px',
      minWidth: 0,
    }) as SxProps<Theme>,

  title: () =>
    ({
      margin: 0,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: 1.25,
    }) as SxProps<Theme>,

  list: () =>
    ({
      display: 'grid',
      gap: '4px',
      margin: 0,
      paddingLeft: '24px',
      fontSize: '16px',
      lineHeight: 1.25,
    }) as SxProps<Theme>,

  refreshButton: (theme: Theme) =>
    ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      border: 0,
      padding: '3px 0',
      background: 'transparent',
      color: 'currentColor',
      fontSize: '14px',
      fontWeight: 400,
      cursor: 'pointer',
      minWidth: 0,
      textTransform: 'none',
      '&.MuiButton-root:hover': {
        background: 'transparent',
      },
      '& svg, & .skill-svg-icon': {
        width: '18px',
        height: '18px',
        fill: 'currentColor',
        stroke: 'none',
      },
      '&:disabled, &.MuiButton-root.Mui-disabled': {
        color: 'currentColor',
        cursor: 'default',
        opacity: 0.6,
      },
      [theme.breakpoints.down('sm')]: {
        gridColumn: 2,
        justifySelf: 'start',
      },
    }) as SxProps<Theme>,

  applyButton: () => applyButtonStyles(),

  anywayButton: () =>
    ({
      justifySelf: 'center',
    }) as SxProps<Theme>,

  applyAnywayButton: () => applyButtonStyles({ justifySelf: 'center' }),

};
